import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Col, Input, Label, Row, Table } from "reactstrap";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";

import SettingsPanel from "../../components/SettingsPanel";
import InfoCard from "../../../../Components/Common/InfoCard/InfoCard";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import {
  slaProfileList,
  slaProfileData,
  slaProfileCreate,
  slaProfileCreateSuccess,
  slaProfileUpdate,
  slaProfileUpdateSuccess,
  slaProfileDelete,
  slaProfileDeleteSuccess,
  slaMetricCreate,
  slaMetricCreateSuccess,
  slaMetricUpdate,
  slaMetricUpdateSuccess,
  slaMetricDelete,
  slaMetricDeleteSuccess,
} from "../../../../store/sla/action";

// Fixed: each unit implies a different calculation (business days skip
// weekends; hours do not), so an unrecognised one could not be acted on.
const UNITS = [
  { value: "", label: "— none —" },
  { value: "Hours", label: "Hours" },
  { value: "BusinessDays", label: "Business days" },
  { value: "Days", label: "Days" },
  { value: "Percent", label: "Percent" },
];

const COMPARISONS = ["<=", "<", ">=", ">", "="];

const EMPTY_PROFILE = { Name: "", Description: "", FlagDefault: 0, FlagActive: 1, Order: "" };
const EMPTY_METRIC = {
  MetricCode: "",
  Description: "",
  TargetValue: "",
  TargetUnit: "",
  TargetComparison: "<=",
  StandardTarget: "",
  SurgeTarget: "",
  Order: "",
};

/**
 * Task 24474 - where SLAs are defined.
 *
 * A profile is one agreed table; its metrics are the rows. Customers are put
 * on a profile from the customer screen (Task 24469), which is what makes
 * "this customer has 6hrs" answerable.
 */
export default function SlaSettings() {
  const dispatch = useDispatch();

  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [profileForm, setProfileForm] = useState(EMPTY_PROFILE);
  const [editingProfile, setEditingProfile] = useState(false);
  const [metricForm, setMetricForm] = useState(EMPTY_METRIC);
  const [editingMetricId, setEditingMetricId] = useState(null);
  const [deleteProfileId, setDeleteProfileId] = useState(null);
  const [deleteMetricId, setDeleteMetricId] = useState(null);

  const {
    profiles,
    profilesLoading,
    profileDetail,
    metrics,
    detailLoading,
    pCreate,
    pUpdate,
    pDelete,
    mCreate,
    mUpdate,
    mDelete,
  } = useSelector((state) => ({
    profiles: state.Sla?.slaProfileList?.slaprofile_list,
    profilesLoading: state.Sla?.slaProfileListLoading,
    profileDetail: state.Sla?.slaProfileData?.slaprofile_data,
    metrics: state.Sla?.slaProfileData?.slametric_list,
    detailLoading: state.Sla?.slaProfileDataLoading,
    pCreate: state.Sla?.slaProfileCreate,
    pUpdate: state.Sla?.slaProfileUpdate,
    pDelete: state.Sla?.slaProfileDelete,
    mCreate: state.Sla?.slaMetricCreate,
    mUpdate: state.Sla?.slaMetricUpdate,
    mDelete: state.Sla?.slaMetricDelete,
  }));

  const reloadProfiles = () => dispatch(slaProfileList());
  const reloadDetail = () => {
    if (selectedProfileId) dispatch(slaProfileData({ id: selectedProfileId }));
  };

  useEffect(() => {
    reloadProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    reloadDetail();
    setMetricForm(EMPTY_METRIC);
    setEditingMetricId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProfileId]);

  const settle = (resp, clear, okMsg, after) => {
    if (isEmpty(resp)) return;
    if (resp.status === 200) {
      toast.success(resp.message || okMsg);
      after?.();
    } else {
      // The delete endpoints refuse an SLA that customers are on, and say how
      // many — surface that rather than a generic failure.
      toast.error(resp.message || "Could not save");
    }
    dispatch(clear([]));
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => settle(pCreate, slaProfileCreateSuccess, "SLA created", () => { setProfileForm(EMPTY_PROFILE); setEditingProfile(false); reloadProfiles(); }), [pCreate]);
  useEffect(() => settle(pUpdate, slaProfileUpdateSuccess, "SLA updated", () => { setProfileForm(EMPTY_PROFILE); setEditingProfile(false); reloadProfiles(); reloadDetail(); }), [pUpdate]);
  useEffect(() => settle(pDelete, slaProfileDeleteSuccess, "SLA deleted", () => { setDeleteProfileId(null); setSelectedProfileId(null); reloadProfiles(); }), [pDelete]);
  useEffect(() => settle(mCreate, slaMetricCreateSuccess, "Commitment added", () => { setMetricForm(EMPTY_METRIC); reloadDetail(); }), [mCreate]);
  useEffect(() => settle(mUpdate, slaMetricUpdateSuccess, "Commitment updated", () => { setMetricForm(EMPTY_METRIC); setEditingMetricId(null); reloadDetail(); }), [mUpdate]);
  useEffect(() => settle(mDelete, slaMetricDeleteSuccess, "Commitment removed", () => { setDeleteMetricId(null); reloadDetail(); }), [mDelete]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const openNewProfile = () => {
    setProfileForm(EMPTY_PROFILE);
    setEditingProfile(true);
  };

  const openEditProfile = (p) => {
    setProfileForm({
      Id: p.Id,
      Name: p.Name || "",
      Description: p.Description || "",
      FlagDefault: String(p.FlagDefault) === "1" ? 1 : 0,
      FlagActive: String(p.FlagActive) === "0" ? 0 : 1,
      Order: p.Order ?? "",
    });
    setEditingProfile(true);
  };

  const saveProfile = () => {
    if (!profileForm.Name.trim()) {
      toast.error("Give the SLA a name");
      return;
    }
    const row = {
      Name: profileForm.Name.trim(),
      Description: profileForm.Description,
      FlagDefault: profileForm.FlagDefault ? 1 : 0,
      FlagActive: profileForm.FlagActive ? 1 : 0,
      Order: profileForm.Order === "" ? null : Number(profileForm.Order),
    };
    if (profileForm.Id) {
      dispatch(slaProfileUpdate([{ Id: profileForm.Id, ...row }]));
    } else {
      dispatch(slaProfileCreate([row]));
    }
  };

  const saveMetric = () => {
    if (!selectedProfileId) return;
    if (!metricForm.Description.trim()) {
      toast.error("Describe the commitment");
      return;
    }
    const row = {
      SlaProfile_Id: selectedProfileId,
      MetricCode: metricForm.MetricCode || null,
      Description: metricForm.Description.trim(),
      TargetValue: metricForm.TargetValue === "" ? null : Number(metricForm.TargetValue),
      TargetUnit: metricForm.TargetUnit || null,
      TargetComparison: metricForm.TargetComparison || null,
      StandardTarget: metricForm.StandardTarget,
      SurgeTarget: metricForm.SurgeTarget,
      Order: metricForm.Order === "" ? null : Number(metricForm.Order),
    };
    if (editingMetricId) {
      dispatch(slaMetricUpdate([{ Id: editingMetricId, ...row }]));
    } else {
      dispatch(slaMetricCreate([row]));
    }
  };

  const startEditMetric = (m) => {
    setEditingMetricId(m.Id);
    setMetricForm({
      MetricCode: m.MetricCode || "",
      Description: m.Description || "",
      TargetValue: m.TargetValue ?? "",
      TargetUnit: m.TargetUnit || "",
      TargetComparison: m.TargetComparison || "<=",
      StandardTarget: m.StandardTarget || "",
      SurgeTarget: m.SurgeTarget || "",
      Order: m.Order ?? "",
    });
  };

  // "within 6 hours" read back out of the machine-readable columns.
  const targetText = (m) => {
    if (m.TargetValue === null || m.TargetValue === undefined || m.TargetValue === "") {
      return "—";
    }
    const unit = UNITS.find((u) => u.value === m.TargetUnit)?.label || m.TargetUnit || "";
    return `${m.TargetComparison || ""} ${Number(m.TargetValue)} ${unit}`.trim();
  };

  return (
    <SettingsPanel
      title="SLAs"
      description="The response and delivery commitments you have agreed, and the compliance targets against them."
      actions={
        <Button color="success" onClick={openNewProfile}>
          <i className="ri-add-line align-bottom me-1" />
          New SLA
        </Button>
      }
    >
      <DeleteModal
        show={!!deleteProfileId}
        onCloseClick={() => setDeleteProfileId(null)}
        onDeleteClick={() => dispatch(slaProfileDelete([deleteProfileId]))}
      />
      <DeleteModal
        show={!!deleteMetricId}
        onCloseClick={() => setDeleteMetricId(null)}
        onDeleteClick={() => dispatch(slaMetricDelete([deleteMetricId]))}
      />

      <Row className="g-3">
        <Col lg={selectedProfileId ? 4 : 12}>
          <InfoCard title="SLA profiles" icon="ri-shield-check-line" accent="indigo">
            {profilesLoading ? (
              <LoadingSpinner />
            ) : isEmpty(profiles) ? (
              <p className="text-muted mb-0">
                No SLAs defined yet. Create one, then add the commitments to it.
              </p>
            ) : (
              <div className="d-flex flex-column gap-2">
                {profiles.map((p) => (
                  <div
                    key={p.Id}
                    className={`d-flex align-items-center gap-2 border rounded p-2 ${
                      String(p.Id) === String(selectedProfileId) ? "border-primary" : ""
                    }`}
                  >
                    <button
                      type="button"
                      className="btn btn-link p-0 text-start flex-grow-1"
                      onClick={() => setSelectedProfileId(p.Id)}
                    >
                      <span className="fw-semibold">{p.Name}</span>
                      {String(p.FlagDefault) === "1" && (
                        <span className="badge bg-primary-subtle text-primary ms-2">
                          Default
                        </span>
                      )}
                      {String(p.FlagActive) === "0" && (
                        <span className="badge bg-secondary-subtle text-secondary ms-1">
                          Inactive
                        </span>
                      )}
                      <div className="text-muted small">
                        {p.QtyMetrics ?? 0} commitment
                        {String(p.QtyMetrics) === "1" ? "" : "s"}
                      </div>
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      title="Edit"
                      onClick={() => openEditProfile(p)}
                    >
                      <i className="ri-pencil-line" />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      title="Delete"
                      onClick={() => setDeleteProfileId(p.Id)}
                    >
                      <i className="ri-delete-bin-line" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </InfoCard>

          {editingProfile && (
            <InfoCard
              title={profileForm.Id ? "Edit SLA" : "New SLA"}
              icon="ri-edit-line"
              accent="violet"
            >
              <Row className="g-2">
                <Col lg={12}>
                  <Label className="mb-1">Name</Label>
                  <Input
                    value={profileForm.Name}
                    onChange={(e) => setProfileForm({ ...profileForm, Name: e.target.value })}
                    placeholder="e.g. Standard SLA"
                  />
                </Col>
                <Col lg={12}>
                  <Label className="mb-1">Description</Label>
                  <Input
                    type="textarea"
                    rows={2}
                    value={profileForm.Description}
                    onChange={(e) =>
                      setProfileForm({ ...profileForm, Description: e.target.value })
                    }
                  />
                </Col>
                <Col lg={6}>
                  <Label className="mb-1">Order</Label>
                  <Input
                    type="number"
                    value={profileForm.Order}
                    onChange={(e) => setProfileForm({ ...profileForm, Order: e.target.value })}
                  />
                </Col>
                <Col lg={12}>
                  <div className="form-check">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="slaDefault"
                      checked={!!profileForm.FlagDefault}
                      onChange={(e) =>
                        setProfileForm({ ...profileForm, FlagDefault: e.target.checked ? 1 : 0 })
                      }
                    />
                    <Label className="form-check-label" for="slaDefault">
                      Default — used for customers with no SLA of their own
                    </Label>
                  </div>
                  <div className="form-check">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="slaActive"
                      checked={!!profileForm.FlagActive}
                      onChange={(e) =>
                        setProfileForm({ ...profileForm, FlagActive: e.target.checked ? 1 : 0 })
                      }
                    />
                    <Label className="form-check-label" for="slaActive">
                      Active
                    </Label>
                  </div>
                </Col>
                <Col lg={12}>
                  <div className="d-flex gap-2">
                    <Button color="success" onClick={saveProfile}>
                      Save
                    </Button>
                    <Button color="light" onClick={() => setEditingProfile(false)}>
                      Cancel
                    </Button>
                  </div>
                </Col>
              </Row>
            </InfoCard>
          )}
        </Col>

        {selectedProfileId && (
          <Col lg={8}>
            <InfoCard
              title={`Commitments — ${profileDetail?.Name || ""}`}
              icon="ri-list-check-2"
              accent="indigo"
              actions={
                <button
                  type="button"
                  className="single-shell-card-btn"
                  onClick={() => setSelectedProfileId(null)}
                >
                  <i className="ri-close-line" />
                  Close
                </button>
              }
            >
              {detailLoading ? (
                <LoadingSpinner />
              ) : isEmpty(metrics) ? (
                <p className="text-muted">No commitments on this SLA yet.</p>
              ) : (
                <div className="table-responsive mb-3">
                  <Table className="align-middle table-wrap mb-0">
                    <thead className="text-muted">
                      <tr>
                        <th>Commitment</th>
                        <th>Target</th>
                        <th>Standard</th>
                        <th>Surge</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {metrics.map((m) => (
                        <tr key={m.Id}>
                          <td>
                            {m.Description}
                            {m.MetricCode && (
                              <div className="text-muted small">
                                <code>{m.MetricCode}</code>
                              </div>
                            )}
                          </td>
                          <td className="text-nowrap">{targetText(m)}</td>
                          <td className="text-nowrap">{m.StandardTarget || "—"}</td>
                          <td className="text-nowrap">{m.SurgeTarget || "—"}</td>
                          <td className="text-end text-nowrap">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary me-1"
                              title="Edit"
                              onClick={() => startEditMetric(m)}
                            >
                              <i className="ri-pencil-line" />
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              title="Remove"
                              onClick={() => setDeleteMetricId(m.Id)}
                            >
                              <i className="ri-delete-bin-line" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              <h6>{editingMetricId ? "Edit commitment" : "Add a commitment"}</h6>
              <Row className="g-2">
                <Col lg={12}>
                  <Label className="mb-1">Description</Label>
                  <Input
                    type="textarea"
                    rows={2}
                    value={metricForm.Description}
                    onChange={(e) =>
                      setMetricForm({ ...metricForm, Description: e.target.value })
                    }
                    placeholder="e.g. Urgent: First Contact to First Visit within 6 hours"
                  />
                </Col>
                <Col lg={2}>
                  <Label className="mb-1">Compare</Label>
                  <Input
                    type="select"
                    value={metricForm.TargetComparison}
                    onChange={(e) =>
                      setMetricForm({ ...metricForm, TargetComparison: e.target.value })
                    }
                  >
                    {COMPARISONS.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col lg={2}>
                  <Label className="mb-1">Value</Label>
                  <Input
                    type="number"
                    value={metricForm.TargetValue}
                    onChange={(e) =>
                      setMetricForm({ ...metricForm, TargetValue: e.target.value })
                    }
                    placeholder="6"
                  />
                </Col>
                <Col lg={3}>
                  <Label className="mb-1">Unit</Label>
                  <Input
                    type="select"
                    value={metricForm.TargetUnit}
                    onChange={(e) =>
                      setMetricForm({ ...metricForm, TargetUnit: e.target.value })
                    }
                  >
                    {UNITS.map((u) => (
                      <option key={u.value} value={u.value}>
                        {u.label}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col lg={5}>
                  <Label className="mb-1">Reference code</Label>
                  <Input
                    value={metricForm.MetricCode}
                    onChange={(e) =>
                      setMetricForm({ ...metricForm, MetricCode: e.target.value })
                    }
                    placeholder="URGENT_FIRST_VISIT"
                  />
                  <small className="text-muted">
                    Optional. Lets the rest of the app find this commitment
                    without matching on its wording.
                  </small>
                </Col>
                <Col lg={3}>
                  <Label className="mb-1">Standard target</Label>
                  <Input
                    value={metricForm.StandardTarget}
                    onChange={(e) =>
                      setMetricForm({ ...metricForm, StandardTarget: e.target.value })
                    }
                    placeholder="95%"
                  />
                </Col>
                <Col lg={3}>
                  <Label className="mb-1">Surge target</Label>
                  <Input
                    value={metricForm.SurgeTarget}
                    onChange={(e) =>
                      setMetricForm({ ...metricForm, SurgeTarget: e.target.value })
                    }
                    placeholder="70%"
                  />
                  <small className="text-muted">
                    Free text — the agreed table also uses “TBA”, “&gt; 70%” and
                    “&lt; 2%”.
                  </small>
                </Col>
                <Col lg={2}>
                  <Label className="mb-1">Order</Label>
                  <Input
                    type="number"
                    value={metricForm.Order}
                    onChange={(e) => setMetricForm({ ...metricForm, Order: e.target.value })}
                  />
                </Col>
                <Col lg={12}>
                  <div className="d-flex gap-2">
                    <Button color="primary" onClick={saveMetric}>
                      <i className="ri-add-line align-bottom me-1" />
                      {editingMetricId ? "Save changes" : "Add commitment"}
                    </Button>
                    {editingMetricId && (
                      <Button
                        color="light"
                        onClick={() => {
                          setEditingMetricId(null);
                          setMetricForm(EMPTY_METRIC);
                        }}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </Col>
              </Row>
            </InfoCard>
          </Col>
        )}
      </Row>
    </SettingsPanel>
  );
}
